/**
 * Is this a real, dialable US phone number?
 *
 * ⚠️ MIRROR of fex-platform-service/src/utils/us-phone.util.ts. Separate repos,
 * so it cannot be imported — change BOTH copies together or the form and the
 * API will disagree. The server copy is authoritative.
 *
 * WHY: the signup phone field only checked SHAPE — ten digits with the NANP
 * "first digit 2-9" rule. That blocks 1111111111 but happily accepts
 * (999) 123-4567, (555) 123-4567 and (354) 534-5345 — the last of which is the
 * number the 2026-08-23 fraudulent signup actually used.
 *
 * The area-code list is OUR OWN `communication_area_codes` table (324 codes,
 * 51 states), exported 2026-08-23. It already backs the area-code picker in
 * signup, so it is data we maintain rather than a third-party list.
 *
 * ⚠️ VALIDATED AGAINST PROD BEFORE SHIPPING: of 484 real users, 20 had an area
 * code outside this list and ALL 20 were junk — 555 (fiction), 123 (illegal),
 * 345 (Cayman, NANP but not US), and nine unassigned codes. Zero real customers
 * would be rejected. Re-run that check before editing this list.
 *
 * ⚠️ Bundled as a constant ON PURPOSE. A service that loads this from the DB is
 * another onModuleInit that can abort app bootstrap — that is exactly what took
 * the backend down on 2026-08-23. NANP adds only a handful of codes a year, so
 * a constant is the right trade. See feedback_new_nest_service_boot_checklist.
 */

/**
 * Every in-service US area code — all 50 states, DC, and the US territories
 * (Puerto Rico 787/939, USVI 340, Guam 671, American Samoa 684, N. Marianas 670).
 *
 * SOURCE: our own communication_area_codes table (324) UNIONED with the current
 * in-service US codes parsed from Wikipedia's NANP list (369). Neither alone was
 * enough:
 *   - our table was missing 51, including EVERY US territory and recent overlays
 *     (943 GA, 948 VA, 983 CO, 645/656/728 FL, 729 TN, 861 IL, 924 MN);
 *   - the popular ravisorg public dataset has only 298 and is missing 26 modern
 *     overlays our table already had (223, 332, 445, 771, 945, ...).
 *
 * ⚠️ Canadian and Caribbean NANP codes are EXCLUDED on purpose — 416 Toronto,
 * 345 Cayman and 284 British Virgin Islands are valid NANP but not US. Note 284
 * is BRITISH VI (excluded) while 340 is US VI (included); a substring match on
 * "Virgin Islands" gets that wrong.
 *
 * ⚠️ 840 (CA, overlay of 909) was missing from BOTH sources: too new for our
 * table, and excluded from the Wikipedia parse because its description names
 * ONTARIO, CALIFORNIA — a naive "Ontario means Canada" filter drops it, along
 * with 909 and 458 (Ontario, Oregon). Match "Ontario, Canada", never "Ontario".
 *
 * ⚠️ REGENERATING: re-parse the source, then re-run the false-positive check
 * against real prod phone numbers before shipping. When this list held only our
 * 324 codes it rejected three REAL customer numbers (324 FL, 353 WI, 748 CO).
 */
const US_AREA_CODES = new Set<number>([201,202,203,205,206,207,208,209,210,212,213,214,215,216,217,218,219,220,223,224,225,227,228,229,231,234,235,239,240,248,251,252,253,254,256,260,262,267,269,270,272,274,276,279,281,283,301,302,303,304,305,307,308,309,310,312,313,314,315,316,317,318,319,320,321,323,324,325,326,327,329,330,331,332,334,336,337,339,340,341,346,347,350,351,352,353,357,360,361,363,364,369,380,385,386,401,402,404,405,406,407,408,409,410,412,413,414,415,417,419,423,424,425,430,432,434,435,440,442,443,445,447,448,457,458,463,464,465,469,470,471,472,475,478,479,480,483,484,501,502,503,504,505,507,508,509,510,512,513,515,516,517,518,520,530,531,534,539,540,541,551,557,559,561,562,563,564,567,570,571,572,573,574,575,580,582,585,586,601,602,603,605,606,607,608,609,610,612,614,615,616,617,618,619,620,621,623,624,626,628,629,630,631,636,640,641,645,646,650,651,656,657,659,660,661,662,667,669,670,671,678,679,680,681,682,684,686,689,701,702,703,704,706,707,708,712,713,714,715,716,717,718,719,720,724,725,726,727,728,729,730,731,732,734,737,738,740,743,747,748,754,757,760,762,763,765,769,770,771,772,773,774,775,779,781,785,786,787,801,802,803,804,805,806,808,810,812,813,814,815,816,817,818,820,821,826,828,830,831,832,835,837,838,839,840,843,845,847,848,850,854,856,857,858,859,860,861,862,863,864,865,870,872,878,901,903,904,906,907,908,909,910,912,913,914,915,916,917,918,919,920,924,925,928,929,930,931,934,936,937,938,939,940,941,943,945,947,948,949,951,952,954,956,959,970,971,972,973,975,978,979,980,983,984,985,986,989]);

/**
 * Toll-free is NOT in the geographic table but is legitimately reachable — an
 * agency owner may well give a business toll-free number as their contact.
 * Allowed deliberately: rejecting these would block real businesses.
 */
const TOLL_FREE = new Set<number>([800, 833, 844, 855, 866, 877, 888]);

export interface PhoneCheck {
  valid: boolean;
  reason?: string;
}

/** Digits only, tolerating a leading +1 / 1. */
export function normalizeUsPhone(raw?: string | null): string {
  let d = String(raw ?? '').replace(/\D/gu, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d;
}

export function checkUsPhone(raw?: string | null): PhoneCheck {
  const d = normalizeUsPhone(raw);

  if (!d) return { valid: false, reason: 'Phone number is required.' };
  if (d.length !== 10) {
    return { valid: false, reason: 'Enter a 10-digit US phone number.' };
  }

  const area = Number(d.slice(0, 3));
  const exchange = Number(d.slice(3, 6));

  // NANP structure: neither the area code nor the exchange may start with 0 or 1.
  if (d[0] === '0' || d[0] === '1' || d[3] === '0' || d[3] === '1') {
    return { valid: false, reason: 'Enter a valid US phone number.' };
  }

  // N11 service codes (411, 911, ...) are never subscriber numbers.
  if (d[1] === '1' && d[2] === '1') {
    return { valid: false, reason: 'Enter a valid US phone number.' };
  }

  // x55 5xxx is the reserved fictional range (555-0100..0199) and 555 as an
  // area code is not assigned at all — both are caught by the list below.
  if (!US_AREA_CODES.has(area) && !TOLL_FREE.has(area)) {
    return { valid: false, reason: `${d.slice(0, 3)} is not a valid US area code.` };
  }

  // Repeated-digit exchange with a repeated line number is placeholder data
  // ("2025551212" style mash), but ONLY when the whole subscriber part is one
  // digit — real numbers like 313-000-0000 do not exist, 313-222-2222 might.
  if (/^(\d)\1{6}$/u.test(d.slice(3))) {
    return { valid: false, reason: 'Enter a valid US phone number.' };
  }

  if (exchange === 555) {
    return { valid: false, reason: 'Enter a valid US phone number.' };
  }

  return { valid: true };
}

export function isValidUsPhone(raw?: string | null): boolean {
  return checkUsPhone(raw).valid;
}
